import React, { useState } from "react";
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import PersonaAutocomplete from "./PersonaAutocomplete";
import DocumentSelectorModal from "./DocumentSelectorModal";

export default function RemissionGuideInfo({
  data,
  onChange,
  onDocumentSelected,
  mobile = false,
}) {
  const [open, setOpen] = useState(false);

  const hasDocument = Boolean(data.documento_relacionado_id);

  const buildGuiaPatch = (patch) => {
    const transportista = patch.guia_transportista ?? data.guia_transportista;

    const fechaInicio =
      patch.guia_fecha_inicio ??
      patch.fecha_emision ??
      data.guia_fecha_inicio ??
      data.fecha_emision ??
      "";

    const fechaFin =
      patch.guia_fecha_fin ??
      data.guia_fecha_fin ??
      fechaInicio ??
      "";

    const guia = {
      transportista_identificacion:
        patch.transportista_identificacion ??
        transportista?.identificacion ??
        transportista?.ruc ??
        transportista?.cedula ??
        "",
      transportista_razon_social:
        patch.transportista_razon_social ??
        transportista?.razon_social ??
        transportista?.nombre_comercial ??
        "",
      placa: patch.guia_placa ?? data.guia_placa ?? "",
      direccion_partida: patch.guia_punto_partida ?? data.guia_punto_partida ?? "",
      direccion_destino: patch.guia_punto_llegada ?? data.guia_punto_llegada ?? "",
      motivo_traslado: patch.guia_motivo_traslado ?? data.guia_motivo_traslado ?? "",
      ruta: patch.guia_ruta ?? data.guia_ruta ?? "",
      fecha_inicio_transporte: fechaInicio,
      fecha_fin_transporte: fechaFin,
      numero_documento_sustento:
        patch.documento_relacionado_numero ??
        data.documento_relacionado_numero ??
        "",
      fecha_documento_sustento:
        patch.documento_relacionado_fecha ??
        data.documento_relacionado_fecha ??
        "",
    };

    onChange?.({
      ...patch,
      fecha_emision: fechaInicio,
      guia_fecha_inicio: fechaInicio,
      guia_fecha_fin: fechaFin,
      guia_remision: guia,
    });
  };

  const handleFechaInicioChange = (e) => {
    const value = e.target.value;

    buildGuiaPatch({
      fecha_emision: value,
      guia_fecha_inicio: value,
    });
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Paper
        sx={{
          p: mobile ? 1.5 : 2,
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          bgcolor: "#fafafa",
        }}
      >
        <Typography sx={{ fontWeight: 800, color: "#333", mb: 1.5 }}>
          Información de guía de remisión
        </Typography>

        <Typography sx={{ fontSize: 13, color: "#666", mb: 1.5 }}>
          En guías de remisión, la fecha de emisión será igual a la fecha de inicio de traslado.
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Button
            variant="contained"
            onClick={() => setOpen(true)}
            sx={{ bgcolor: "#1565C0", alignSelf: mobile ? "stretch" : "flex-start" }}
          >
            Seleccionar documento sustento
          </Button>

          {hasDocument ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <TextField
                label="Documento sustento"
                value={data.documento_relacionado_numero || ""}
                InputProps={{ readOnly: true }}
                fullWidth={mobile}
                sx={{ minWidth: mobile ? "100%" : 350 }}
              />

              <TextField
                label="Clave de acceso"
                value={data.documento_relacionado_clave_acceso || ""}
                InputProps={{ readOnly: true }}
                fullWidth
              />
            </Box>
          ) : (
            <Typography sx={{ fontSize: 13, color: "#777" }}>
              Puedes seleccionar una factura autorizada como documento sustento.
            </Typography>
          )}

          <TextField
            label="Motivo de traslado"
            value={data.guia_motivo_traslado || ""}
            onChange={(e) => buildGuiaPatch({ guia_motivo_traslado: e.target.value })}
            fullWidth
          />

          <TextField
            label="Punto de partida"
            value={data.guia_punto_partida || ""}
            onChange={(e) => buildGuiaPatch({ guia_punto_partida: e.target.value })}
            fullWidth
          />

          <TextField
            label="Punto de llegada"
            value={data.guia_punto_llegada || ""}
            onChange={(e) => buildGuiaPatch({ guia_punto_llegada: e.target.value })}
            fullWidth
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: mobile ? "1fr" : "1fr 1fr",
              gap: 1.5,
            }}
          >
            <TextField
              label="Fecha inicio traslado"
              value={data.guia_fecha_inicio || data.fecha_emision || ""}
              onChange={handleFechaInicioChange}
              placeholder="dd/mm/aaaa"
              inputProps={{ maxLength: 10 }}
              helperText="También será la fecha de emisión de la guía"
              fullWidth
            />

            <TextField
              label="Fecha fin traslado"
              value={data.guia_fecha_fin || ""}
              onChange={(e) => buildGuiaPatch({ guia_fecha_fin: e.target.value })}
              placeholder="dd/mm/aaaa"
              inputProps={{ maxLength: 10 }}
              fullWidth
            />
          </Box>

          <Box>
            <Typography sx={{ fontWeight: 700, color: "#333", mb: 0.75 }}>
              Transportista
            </Typography>

            <PersonaAutocomplete
              value={data.guia_transportista}
              tipoDocumento="PROVEEDOR"
              onChange={(persona) =>
                buildGuiaPatch({
                  guia_transportista: persona,
                  transportista_identificacion:
                    persona?.identificacion || persona?.ruc || persona?.cedula || "",
                  transportista_razon_social:
                    persona?.razon_social || persona?.nombre_comercial || "",
                })
              }
              mobile={mobile}
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: mobile ? "1fr" : "1fr 1fr",
              gap: 1.5,
            }}
          >
            <TextField
              label="Placa"
              value={data.guia_placa || ""}
              onChange={(e) => buildGuiaPatch({ guia_placa: e.target.value.toUpperCase() })}
              fullWidth
            />

            <TextField
              label="Ruta"
              value={data.guia_ruta || ""}
              onChange={(e) => buildGuiaPatch({ guia_ruta: e.target.value })}
              fullWidth
            />
          </Box>
        </Box>
      </Paper>

      <DocumentSelectorModal
        open={open}
        onClose={() => setOpen(false)}
        persona={data.persona}
        title="Seleccionar documento sustento"
        tipo="FACTURA"
        endpoint="/facturas/documentos-relacionados/facturas-cliente/"
        detailEndpoint="/facturas/documentos-relacionados/detalle-factura/"
        onSelected={onDocumentSelected}
      />
    </Box>
  );
}